import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onSummaryClick);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onSummaryClick);
  }

  /**
   * Delegated click: when a summary of a details with data-menu-item-handle and
   * .menu-drawer__mega-content is clicked, run mega/fallback logic after the native toggle.
   * (The toggle event does not bubble, so we use click and setTimeout to run after it.)
   * @param {MouseEvent} event
   */
  #onSummaryClick = (event) => {
    const summary = event.target instanceof Element ? event.target.closest('summary') : null;
    if (!summary) return;

    const details = summary.parentElement;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (!details.hasAttribute('data-menu-item-handle')) return;
    if (!details.querySelector('.menu-drawer__mega-content')) return;

    setTimeout(() => this.#applyMegaMenuState(details), 0);
  };

  /**
   * For a details with data-menu-item-handle and .menu-drawer__mega-content: when open,
   * inject the matching mega-menu section and hide fallback; when closed, clear mega and show fallback.
   * @param {HTMLDetailsElement} details
   */
  #applyMegaMenuState(details) {
    const megaContent = details.querySelector('.menu-drawer__mega-content');
    const fallback = details.querySelector('.menu-drawer__fallback-links');
    if (!megaContent) return;

    const handle = (details.dataset.menuItemHandle || '').trim().toLowerCase();
    if (!handle) {
      megaContent.innerHTML = '';
      megaContent.classList.remove('menu-drawer__mega-content--loaded');
      megaContent.setAttribute('aria-hidden', 'true');
      if (fallback) fallback.removeAttribute('hidden');
      return;
    }

    if (details.open) {
      const scope = document.getElementById('header-group') || document;
      let megaSection = null;
      for (const el of scope.querySelectorAll('[data-section-type="mega-menu"][data-menu-item-handle]')) {
        const h = (el.getAttribute('data-menu-item-handle') || '').trim().toLowerCase();
        if (h !== handle) continue;
        const t = (el.getAttribute('data-mega-target') || 'both').toLowerCase();
        if (t === 'desktop') continue;
        megaSection = el;
        break;
      }
      if (!megaSection) {
        const byId = document.getElementById(`mega-menu-${handle}`);
        if (byId?.getAttribute('data-section-type') === 'mega-menu') {
          const t = (byId.getAttribute('data-mega-target') || 'both').toLowerCase();
          if (t !== 'desktop') megaSection = byId;
        }
      }
      const src =
        megaSection?.querySelector('.mega-menu-section-hidden') ||
        megaSection?.querySelector('.mega-menu-section-preview-content');
      if (src?.innerHTML?.trim()) {
        megaContent.innerHTML = src.innerHTML;
        megaContent.classList.add('menu-drawer__mega-content--loaded');
        megaContent.removeAttribute('aria-hidden');
        if (fallback) fallback.setAttribute('hidden', '');
        return;
      }
      megaContent.innerHTML = '';
      megaContent.classList.remove('menu-drawer__mega-content--loaded');
      megaContent.setAttribute('aria-hidden', 'true');
      if (fallback) fallback.removeAttribute('hidden');
    } else {
      megaContent.innerHTML = '';
      megaContent.classList.remove('menu-drawer__mega-content--loaded');
      megaContent.setAttribute('aria-hidden', 'true');
      if (fallback) fallback.removeAttribute('hidden');
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
